import { useEffect, useRef, useState } from "react";
import { Info, Sparkles, X } from "lucide-react";
import { api, C } from "../lib/api";

const ROLL_CENTS_PER_SEC = 20;
export const fmtCents = (c) => `$${Math.floor((c || 0) / 100).toLocaleString()}.${String(Math.abs(c || 0) % 100).padStart(2, "0")}`;

// Odometer digit: a 0-9 strip that rolls vertically to the current figure (tumbler style)
const Digit = ({ d }) => (
  <span className="odo-digit"><span className="odo-strip" style={{ transform: `translateY(-${d * 10}%)` }}>{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <span key={n}>{n}</span>)}</span></span>
);
const Odometer = ({ cents }) => {
  const dollars = Math.floor((cents || 0) / 100).toLocaleString();
  const cc = String(Math.abs(cents || 0) % 100).padStart(2, "0");
  return (
    <span className="inline-flex items-baseline">
      <span>$</span>{dollars.split("").map((ch, i) => (/\d/.test(ch) ? <Digit key={`d${i}`} d={+ch} /> : <span key={`s${i}`}>{ch}</span>))}
      <span>.</span><span className="odo-cents inline-flex"><Digit d={+cc[0]} /><Digit d={+cc[1]} /></span>
    </span>
  );
};

// How-to-win card behind the little (?) — fetched once, cached for the session.
let HOW = null;
const JackpotInfo = ({ onClose }) => {
  const [how, setHow] = useState(HOW);
  useEffect(() => { api.get(C.ApiRoutes.JACKPOT).then((r) => { HOW = r.data; setHow(r.data); }).catch(() => {}); }, []);
  return (
    <div data-testid="jackpot-info" className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[22rem] z-50 rounded-xl border border-amber-400/50 bg-black/95 p-4 text-left shadow-[0_0_30px_rgba(230,185,76,0.35)]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-xs tracking-[0.3em] text-amber-300">HOW THE JACKPOT IS WON</div>
        <button data-testid="jackpot-info-close" onClick={onClose} className="text-slate-400 hover:text-amber-200"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-[11px] text-slate-400 mb-2">One jackpot per stake level — Hold'em and PLO cash rooms only · fed by {how?.feeds || "0.05% of every rake in the rooms that feed it"} · both hole cards must play, and the hand has to be there on the flop.{how?.seed_rule ? ` Pools ${how.seed_rule}.` : ""}</p>
      {how?.pools?.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mb-3" data-testid="jackpot-pools">
          {how.pools.map((p) => <li key={p.key} className="rounded-full border border-amber-400/30 px-2 py-0.5 text-[10px]"><span className="text-slate-300">{p.stakes}</span> <span className="font-mono text-amber-200">{fmtCents(p.amount_cents)}</span></li>)}
        </ul>
      )}
      <ul className="flex flex-col gap-2">
        {(how?.how_to_win || []).map((h) => (
          <li key={h.name} className="text-xs"><span className="text-amber-200 font-display">{h.name}</span> <span className="font-mono text-emerald-300">{h.pct}%</span><div className="text-slate-300">{h.rule}</div></li>
        ))}
      </ul>
      <p data-testid="jackpot-excludes" className="text-[11px] text-amber-200/80 mt-3 border-t border-amber-400/20 pt-2">{how?.excludes || "Double Board Bomb Pots don't qualify for the jackpot."}</p>
    </div>
  );
};

// Casino sign: angled gold frame with pulsing bulbs, digits (with cents) that tick up to the live value over the poll interval.
export const JackpotBox = ({ cents, compact = false, testId = "jackpot-box", label = null }) => {
  const [shown, setShown] = useState(cents || 0);
  const [info, setInfo] = useState(false);
  // continuous roll: the display tumbles towards the server value at a constant pace (never ahead of the truth)
  const ref = useRef({ shown: cents || 0, target: cents || 0 });
  useEffect(() => {
    const r = ref.current, target = cents || 0;
    if (target < r.shown) { r.shown = target; setShown(target); }  // payout / reset: snap down
    r.target = target;
  }, [cents]);
  useEffect(() => {
    let raf, prev = performance.now();
    const step = (t) => {
      const r = ref.current, dt = (t - prev) / 1000; prev = t;
      if (r.shown < r.target) {
        const gap = r.target - r.shown;
        // constant tumble: 20¢/s (cents roll, a dollar every 5 s, $10 every 50 s); only speeds up if the display has fallen > 2 min behind
        const rate = Math.max(ROLL_CENTS_PER_SEC, gap / 120);
        r.shown = Math.min(r.target, r.shown + rate * dt);
        setShown(Math.floor(r.shown));
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);
  useEffect(() => {
    if (!info) return;
    const close = () => setInfo(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [info]);
  return (
    <div className="relative inline-flex items-center gap-1.5">
      <div className="jackpot-halo">
        <div data-testid={testId} className={`jackpot-sign relative select-none ${compact ? "h-10 px-9" : "h-16 px-14"}`}>
          <div className="jackpot-sign-frame" aria-hidden />
          <div className="jackpot-bulbs" aria-hidden />
          <div className="relative h-full flex items-center gap-4">
            <div className={`jackpot-word font-display ${compact ? "text-sm tracking-[0.3em]" : "text-2xl tracking-[0.35em]"} flex items-center gap-1.5`}><Sparkles className={compact ? "w-3.5 h-3.5 text-amber-300" : "w-5 h-5 text-amber-300"} /> <span className="jackpot-shimmer">JACKPOT</span>{label && <span data-testid={`${testId}-label`} className={`font-mono tracking-normal text-amber-200/80 ${compact ? "text-[10px]" : "text-xs"} ml-1`}>{label}</span>}</div>
            <div data-testid={`${testId}-amount`} className={`jackpot-digits font-mono ${compact ? "text-lg" : "text-3xl"} leading-none`} data-cents={shown}><Odometer cents={shown} /></div>
          </div>
          <div className="jackpot-sweep" aria-hidden />
        </div>
      </div>
      <button data-testid={`${testId}-info`} onClick={(e) => { e.stopPropagation(); setInfo((v) => !v); }} title="How the jackpot is won"
        className="w-4 h-4 rounded-full border border-amber-300/50 text-amber-300/70 hover:text-amber-200 hover:border-amber-200 flex items-center justify-center"><Info className="w-2.5 h-2.5" /></button>
      {info && <JackpotInfo onClose={() => setInfo(false)} />}
    </div>
  );
};
